import { useEffect, useState, type ReactNode } from "react";
import { Btn, Card, Dropdown, Icon, Pill, T, Toggle } from "@v0y4ge/jrni-ui";
import { useNav } from "../../shell/router";
import { Screen, PageHeader } from "../../shell/chrome";
import { useAutosave, AutosaveBadge } from "../../shell/editor-hooks";
import { can, isAdmin } from "../../shell/access";
import { toast } from "../../shell/toast";
import { TicketsSubTabs } from "./TicketsSubtabs";
import { eventsSectionTabs } from "../../shell/list-composites";
import { useEventSectionCounts } from "../events/eventSectionCounts";
import { TicketTypeStore, useTicketTypes, TT_KINDS, TT_PALETTE, ttKindPill, ttMoney, ttInput, ttLabel, type TicketType } from "./ticket-types-store";
import { useTicketGroups } from "./ticket-groups-store";
import { NumberField } from "../../lib/NumberField";
import { useT, t as i18nT } from "../../lib/i18n";

/**
 * One field = label · control · hint. Before this the fields were bare
 * label+input pairs with nothing explaining them, so "Kind" in particular gave
 * no clue what picking it changes.
 *
 * REQ-106 filed for the new keys (nameHint / descHint / colourHint /
 * lockedPriceHint / kindHint.*) — the catalog is kit-owned, so they render from
 * the `t()` fallback until Claude Code ships them.
 */
function TtField({ label, hint, children }: { label: ReactNode; hint?: ReactNode; children: ReactNode }) {
  return (
    <div>
      <div style={ttLabel}>{label}</div>
      {children}
      {hint ? <div style={{ fontSize: 12, color: T.soft, lineHeight: 1.45, marginTop: 6, textWrap: "pretty" }}>{hint}</div> : null}
    </div>
  );
}

/** What actually happens at checkout for the selected kind. */
function kindHint(kind: TicketType["kind"], t: (k: string, fb?: string) => string): string {
  if (kind === "paid") return t("tickets.typeEditorPage.kindHint.paid", "Guests pay at checkout. The price is set per event unless you lock it below.");
  if (kind === "donation") return t("tickets.typeEditorPage.kindHint.donation", "Guests choose their own amount at checkout — no fixed price.");
  if (kind === "invite") return t("tickets.typeEditorPage.kindHint.invite", "Hidden from public pages. Only guests you invite can select it.");
  return t("tickets.typeEditorPage.kindHint.free", "No payment step — guests just register.");
}

export default function TicketTypeEditorPage() {
  const t = useT();
  const nav = useNav();
  const isNew = !!(nav.params && nav.params.new);
  const viewOnly = !!(nav.params && nav.params.view);
  const id = nav.params && (nav.params.id as string | undefined);
  const canWrite = !viewOnly && can("events.write");

  const seed = (): TicketType => isNew ? TicketTypeStore.newType() : (id && TicketTypeStore.get(id) ? JSON.parse(JSON.stringify(TicketTypeStore.get(id))) : TicketTypeStore.newType());
  const [form, setForm] = useState<TicketType>(seed);
  const routeKey = `${isNew ? "new" : id || ""}:${viewOnly ? "v" : "e"}`;
  useEffect(() => { setForm(seed()); }, [routeKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (patch: Partial<TicketType>) => setForm((f) => ({ ...f, ...patch }));
  const admin = isAdmin();
  const locked = !!form.priceLocked;
  // Price only lives on the type when an admin locks it; otherwise it's set per
  // event in the create wizard.
  const saveDisabled = !form.name.trim() || (locked && !(form.price > 0));
  const pill = ttKindPill(form.kind);
  const priceLabel = locked ? ttMoney(form.price) : "Set per event";
  const autoStatus = useAutosave({ enabled: !viewOnly && !saveDisabled, data: form, save: () => TicketTypeStore.upsert({ ...form, price: locked ? Number(form.price) || 0 : 0 }) });

  const onSave = async () => {
    const clean = { ...form, price: locked ? Number(form.price) || 0 : 0 };
    try {
      await TicketTypeStore.upsert(clean);
      toast({ tone: "success", title: isNew ? "Ticket type created" : "Ticket type saved", subtitle: i18nT("tickets.typeEditorPage.cleanName.icu", { value0: clean.name }) });
      nav.go("ticket-types");
    } catch (e) {
      console.warn("ticket-type save failed:", e);
      toast({ tone: "danger", title: t("tickets.typeEditorPage.couldnTSaveTicketType", "Couldn't save ticket type"), subtitle: t("tickets.typeEditorPage.nothingWasSavedYourEditsAreStillHereRetryInAMoment", "Nothing was saved — your edits are still here. Retry in a moment.") });
    }
  };

  return (
    <Screen rail="events" tabs={eventsSectionTabs(nav, "tickets", useEventSectionCounts())} activeTab="tickets">
      <TicketsSubTabs nav={nav} active="types" typesCount={useTicketTypes().length} groupsCount={useTicketGroups().length} />
      <PageHeader
        title={isNew ? t("tickets.typePicker.newTicketType", "New ticket type") : (viewOnly ? form.name || t("eventday.eventDay.ticketType", "Ticket type") : t("tickets.ticketTypeEditorPage.editFormNameTicketType", { value0: form.name || t("eventday.eventDay.ticketType", "Ticket type") }))}
        subtitle={<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Pill tone={pill.tone}>{pill.label}</Pill>{locked ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icon.lock size={12} /> {priceLabel}</span> : priceLabel}</span>}
        breadcrumb={<span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("ticket-types")}><Icon.arrow dir="left" size={11} /> {t("tickets.typeEditorPage.ticketTypes", "Ticket types")}</span>}
        actions={viewOnly ? <Btn icon={<Icon.edit size={14} />} onClick={() => nav.go("ticket-types.edit", { id: form.id })}>{t("common.actions.edit", "Edit")}</Btn> : <>
          <AutosaveBadge status={autoStatus} />
          <Btn variant="secondary" onClick={() => nav.go("ticket-types")}>{t("common.actions.cancel", "Cancel")}</Btn>
          <Btn icon={<Icon.check size={14} />} disabled={saveDisabled} onClick={onSave}>{isNew ? t("tickets.ticketTypeEditorPage.createType", "Create type") : t("account.details.saveChanges", "Save changes")}</Btn>
        </>}
      />
      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "minmax(420px,1fr) minmax(320px,400px)", minHeight: 0, borderTop: `1px solid ${T.border}`, marginTop: 16 }}>
        <div style={{ borderRight: `1px solid ${T.border}`, background: T.canvas, overflow: "auto", padding: "20px 24px 40px", opacity: canWrite ? 1 : 0.7, pointerEvents: canWrite ? "auto" : "none" }}>
          {/* Explains the model BEFORE the fields, never after them (operator,
              2026-07-30): read at the end it is a footnote, read first it is the
              thing that makes "no capacity here" make sense. Multi-line, so the
              icon top-aligns. */}
          <div style={{ marginBottom: 12, padding: 12, background: T.p4, border: `1px solid ${T.p3}`, borderRadius: 8, fontSize: 12.5, color: T.n1, lineHeight: 1.5, display: "flex", gap: 8, alignItems: "flex-start" }}>
            <span style={{ color: T.p1, flexShrink: 0, marginTop: 1, display: "inline-flex" }}><Icon.info size={14} /></span>
            <span>{t("tickets.typeEditorPage.aTicketTypeHasNoCapacityOfItsOwnEachEventSets", "A ticket type has no capacity of its own — each event sets how many to release when the type is used in a group. Sold counts start at zero and fill as people register.")}</span>
          </div>
          <Card style={{ padding: 18, display: "flex", flexDirection: "column", gap: 16 }}>
            <TtField label={t("tickets.typeEditorPage.typeName", "Type name")} hint={t("tickets.typeEditorPage.nameHint", "Guests see this at checkout — keep it short, like “VIP” or “Early bird”.")}>
              <input autoFocus={isNew} value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder={t("tickets.typeEditorPage.eGGeneralAdmissionVip", "e.g. General admission, VIP")} style={ttInput()} />
            </TtField>
            <TtField label={t("tickets.typeEditorPage.description", "Description")} hint={t("tickets.typeEditorPage.descHint", "One line on what the ticket includes. Shown under the name on the event page.")}>
              <input value={form.desc} onChange={(e) => set({ desc: e.target.value })} placeholder={t("tickets.typeEditorPage.whatThisTicketIncludes", "What this ticket includes")} style={ttInput()} />
            </TtField>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignItems: "start" }}>
              {/* The kind hint is per-VALUE, not generic: it answers "what happens at
                  checkout if I pick this?", which is the actual question. */}
              <TtField label={t("tickets.typeEditorPage.kind", "Kind")} hint={kindHint(form.kind, t)}>
                <Dropdown
                  options={TT_KINDS.map((k) => ({ value: k.id, label: k.label }))}
                  value={form.kind}
                  onChange={(v) => set({ kind: v as TicketType["kind"] })}
                  aria-label={t("tickets.typeEditorPage.kind", "Kind")}
                  testid="tt-kind"
                  style={{ gap: 0, width: "100%" }}
                />
              </TtField>
              <TtField label={t("tickets.typeEditorPage.colour", "Colour")} hint={t("tickets.typeEditorPage.colourHint", "Tints this ticket wherever it appears — lists, the preview, the event page.")}>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", paddingTop: 2 }}>
                  {TT_PALETTE.map((c) => (
                    /* ui-raw: colour swatch — ColorPicker's pattern (group 10) */
                    <button key={c} type="button" onClick={() => set({ color: c })} aria-label={i18nT("tickets.typeEditorPage.colourC.icu", { value0: c })} style={{ width: 26, height: 26, borderRadius: 7, background: c, border: form.color === c ? `2px solid ${T.n1}` : "2px solid transparent", cursor: "pointer" }} />
                  ))}
                </div>
              </TtField>
            </div>
          </Card>
          {admin ? (
            <Card style={{ padding: 18, marginTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <span style={{ color: T.p1, marginTop: 2, flexShrink: 0 }}><Icon.lock size={16} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{t("tickets.typeEditorPage.lockPricing", "Lock pricing")}<span style={{ fontSize: 11, fontWeight: 600, color: T.p1, background: T.p4, borderRadius: 999, padding: "1px 8px", marginLeft: 4 }}>{t("tickets.typeEditorPage.admin", "Admin")}</span></div>
                  <div style={{ fontSize: 12.5, color: T.soft, marginTop: 3, lineHeight: 1.5 }}>{t("tickets.typeEditorPage.setAFixedPriceAtTheTicketTypeLevelWhenLockedCoordinators", "Set a fixed price at the ticket-type level. When locked, coordinators can’t change this ticket’s price in the event create wizard.")}</div>
                </div>
                <Toggle on={locked} onChange={canWrite ? (v) => set({ priceLocked: v }) : undefined} />
              </div>
              {locked ? (
                <TtField label={t("tickets.typeEditorPage.lockedPrice", "Locked price")} hint={t("tickets.typeEditorPage.lockedPriceHint", "Applies to every event that uses this type. Coordinators can’t override it while the lock is on.")}>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 14, color: T.soft }}>$</span>
                    <NumberField value={form.price} onChange={(n) => set({ price: n })} decimal disabled={!canWrite} style={{ width: 120 }} />
                  </div>
                </TtField>
              ) : null}
            </Card>
          ) : null}
        </div>
        <div style={{ background: T.canvas, overflow: "auto", padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: 0.5 }}>{t("tickets.typeEditorPage.preview", "Preview")}</div>
          <Card style={{ padding: 0, overflow: "hidden", flexShrink: 0 }}>
            <div style={{ height: 6, background: form.color }} />
            <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: T.n1 }}>{form.name || "Untitled type"}</div>
                <span style={{ fontSize: 16, fontWeight: 700, color: T.n1, display: "inline-flex", alignItems: "center", gap: 4 }}>{locked ? <><Icon.lock size={13} /> {ttMoney(form.price)}</> : t('tickets.typeEditorPage.text')}</span>
              </div>
              <p style={{ fontSize: 13, color: T.soft, margin: 0, lineHeight: 1.5 }}>{form.desc || "No description yet."}</p>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}><Pill tone={pill.tone}>{pill.label}</Pill></div>
            </div>
          </Card>
        </div>
      </div>
    </Screen>
  );
}
